/* === Pile des calques ouverts (Échap) === */

/** Ce que la pile lit d'un calque au moment de la frappe. */
export interface GestionnaireCalque {
  onEscape: () => void;
  /** Faux : le calque absorbe Échap sans rien fermer (fermeture interdite). */
  actif: boolean;
}

export interface OptionsCalque {
  /**
   * Calque qui reste ouvert pendant qu'on travaille ailleurs (panneau de
   * l'assistant) : il remonte au sommet quand le focus entre dans son
   * `conteneur`.
   */
  persistant?: boolean;
  /** Élément du calque, lu à chaque `focusin` (calque persistant seulement). */
  conteneur?: () => HTMLElement | null;
}

interface Calque {
  id: string;
  /** Lu à chaque frappe : changer `onEscape` ne déplace pas le calque dans la pile. */
  lire: () => GestionnaireCalque;
  persistant: boolean;
  conteneur?: () => HTMLElement | null;
}

/**
 * Ordre des calques ouverts qui se ferment sur Échap — fenêtres (`AgModal`),
 * panneaux (`AgDrawer` hors `permanent`), puis menus, popovers, feuilles et
 * calques maison, par `useCalqueEchap`. Le dernier ouvert est au sommet ; un
 * calque **persistant** (le panneau de l'assistant) remonte au sommet quand le
 * focus entre dans son conteneur : « je tape dans l'assistant, j'appuie sur
 * Échap » ferme l'assistant, pas la fenêtre peinte dessous. Un menu ouvert
 * ensuite depuis l'assistant se place au-dessus de lui.
 *
 * Un seul écouteur `keydown` sur `window`, en bouillonnement, porté par la
 * pile (posé au premier calque, retiré quand la pile se vide) : à chaque
 * Échap, il n'appelle que le gestionnaire du calque au sommet **tel qu'il est
 * au moment de la frappe**. Une frappe ne ferme donc qu'un calque, même si
 * React démonte celui du dessus pendant l'événement ; et un calque du dessus
 * inactif (`closeOnEsc={false}`, `closeDisabled`) absorbe la frappe sans rien
 * fermer (Échap ne « traverse » pas vers celui du dessous). Un second
 * écouteur, `focusin` sur `window` en capture, fait remonter les calques
 * persistants.
 *
 * Ce que la pile laisse passer sans rien fermer : une frappe qu'un élément
 * interne (liste déroulante, autocomplétion, éditeur) a déjà traitée — arrêtée
 * par `stopPropagation` (elle n'atteint pas `window`) ou marquée par
 * `preventDefault` (listes de mentions, menus de l'éditeur) — et une frappe
 * pendant une composition IME (`isComposing`).
 *
 * Les identifiants sont retirés par valeur : un calque du milieu peut se
 * démonter avant celui du dessus, et React StrictMode rejoue montage,
 * démontage, montage avec le même identifiant sans laisser de doublon.
 */
const pile: Calque[] = [];

function surToucheEchap(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
  const sommet = pile[pile.length - 1];
  if (!sommet) return;
  const { onEscape, actif } = sommet.lire();
  if (actif) onEscape();
}

function surFocusIn(e: FocusEvent): void {
  const cible = e.target;
  if (!(cible instanceof Node)) return;
  // Du sommet vers le bas : le premier calque persistant qui contient la cible
  // passe au sommet (un panneau imbriqué dans un autre l'emporte s'il est au-dessus).
  for (let i = pile.length - 1; i >= 0; i -= 1) {
    const calque = pile[i];
    if (!calque.persistant) continue;
    const conteneur = calque.conteneur?.();
    if (!conteneur || !conteneur.contains(cible)) continue;
    if (i !== pile.length - 1) {
      pile.splice(i, 1);
      pile.push(calque);
    }
    return;
  }
}

function poserEcouteurs(): void {
  window.addEventListener('keydown', surToucheEchap);
  window.addEventListener('focusin', surFocusIn, true);
}

function retirerEcouteurs(): void {
  window.removeEventListener('keydown', surToucheEchap);
  window.removeEventListener('focusin', surFocusIn, true);
}

/** Vrai tant qu'au moins un calque est ouvert. */
export function calqueOuvert(): boolean {
  return pile.length > 0;
}

/** Identifiant du calque au sommet (tests). */
export function calqueAuSommet(): string | null {
  return pile.length > 0 ? pile[pile.length - 1].id : null;
}

export function empilerCalque(
  id: string,
  lire: () => GestionnaireCalque,
  options?: OptionsCalque,
): void {
  retirerCalque(id);
  if (pile.length === 0) poserEcouteurs();
  pile.push({
    id,
    lire,
    persistant: options?.persistant ?? false,
    conteneur: options?.conteneur,
  });
}

export function retirerCalque(id: string): void {
  const index = pile.findIndex((calque) => calque.id === id);
  if (index === -1) return;
  pile.splice(index, 1);
  if (pile.length === 0) retirerEcouteurs();
}
